import { Store } from '@website/Store';
import Link from 'next/link';
import { useContext } from 'react';
import { Breadcrumb, Button, Container, Nav } from 'react-bootstrap';

interface TripFormLayoutProps {
  baseUrl: string;
  children: JSX.Element;
  host: {
    name: string;
    urlSlug: string;
  };
  tab: string;
  title: string;
  tripUrl: string;
}

export default function TripFormLayout({
  baseUrl,
  children,
  host,
  tab,
  title,
  tripUrl,
}: TripFormLayoutProps) {
  const { state } = useContext(Store);

  const isAdmin = state.user?.roles?.includes('admin');

  return (
    <Container className="pt-3">
      <Breadcrumb listProps={{ className: 'ps-0 justify-content-start' }}>
        <Link href={`/hosts/dashboard/${host.urlSlug}`} passHref legacyBehavior>
          <Breadcrumb.Item>{host.name}</Breadcrumb.Item>
        </Link>

        <Link href={`/trips/${tripUrl}`} passHref legacyBehavior>
          <Breadcrumb.Item>{title}</Breadcrumb.Item>
        </Link>

        <Link href={`/trips/${tripUrl}/edit`} passHref legacyBehavior>
          <Breadcrumb.Item active>Edit</Breadcrumb.Item>
        </Link>
      </Breadcrumb>

      <h1>Edit {title}</h1>

      <Nav variant="tabs" className="mb-3">
        <Link href="/trips/[tripUrl]/edit" as={baseUrl} passHref legacyBehavior>
          <Nav.Link active={tab === 'trip'}>Trip</Nav.Link>
        </Link>
        <Link
          href="/trips/[tripUrl]/edit/media"
          as={`${baseUrl}/media`}
          passHref
          legacyBehavior
        >
          <Nav.Link active={tab === 'media'} eventKey={`${baseUrl}/media`}>
            Media
          </Nav.Link>
        </Link>
        <Link
          href="/trips/[tripUrl]/edit/itinerary"
          as={`${baseUrl}/itinerary`}
          passHref
          legacyBehavior
        >
          <Nav.Link
            active={tab === 'itinerary'}
            eventKey={`${baseUrl}/itinerary`}
          >
            Itinerary
          </Nav.Link>
        </Link>

        <Link
          href="/trips/[tripUrl]/edit/inclusions"
          as={`${baseUrl}/inclusions`}
          passHref
          legacyBehavior
        >
          <Nav.Link
            active={tab === 'inclusions'}
            eventKey={`${baseUrl}/inclusions`}
          >
            Inclusions
          </Nav.Link>
        </Link>

        <Link
          href="/trips/[tripUrl]/edit/faqs"
          as={`${baseUrl}/faqs`}
          passHref
          legacyBehavior
        >
          <Nav.Link active={tab === 'faqs'}>Faqs</Nav.Link>
        </Link>
        <Link
          href="/trips/[tripUrl]/edit/notices"
          as={`${baseUrl}/notices`}
          passHref
          legacyBehavior
        >
          <Nav.Link active={tab === 'notices'}>NB Notices</Nav.Link>
        </Link>
        <Link
          href="/trips/[tripUrl]/edit/groups"
          as={`${baseUrl}/groups`}
          passHref
          legacyBehavior
        >
          <Nav.Link active={tab === 'groups'}>Groups</Nav.Link>
        </Link>
        <Link
          href="/trips/[tripUrl]/edit/trip-form-settings"
          as={`${baseUrl}/trip-form-settings`}
          passHref
          legacyBehavior
        >
          <Nav.Link active={tab === 'trip-form-settings'}>Trip Forms</Nav.Link>
        </Link>
        <Link
          href="/trips/[tripUrl]/edit/trip-automated-message"
          as={`${baseUrl}/trip-automated-message`}
          passHref
          legacyBehavior
        >
          <Nav.Link active={tab === 'trip-automated-message'}>
            Automated Message
          </Nav.Link>
        </Link>
        <Link
          href="/trips/[tripUrl]/edit/meet-points"
          as={`${baseUrl}/meet-points`}
          passHref
          legacyBehavior
        >
          <Nav.Link active={tab === 'meet-points'}>Way Points</Nav.Link>
        </Link>
        {isAdmin && (
          <Link
            href="/trips/[tripUrl]/edit/admin"
            as={`${baseUrl}/admin`}
            passHref
            legacyBehavior
          >
            <Nav.Link active={tab === 'admin'}>Admin</Nav.Link>
          </Link>
        )}
      </Nav>
      {children}
      <div className="d-grid gap-2">
        <Link href="/trips/[tripUrl]" as={`/trips/${tripUrl}`} legacyBehavior>
          <Button variant="dark" className="mt-2">
            Back to trip
          </Button>
        </Link>
      </div>
    </Container>
  );
}
